import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Grid, Group, LoadingOverlay } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { MainLayout } from '../../layouts'
import { ContentBox, BackListButton, SaveButton, ResetButton } from '../../partials'
import { FormTypes } from '../../../types'
import { useAppDispatch, useAppSelector } from '../../../store'
import { IInvoiceParams, InvoiceType } from '../../../store/types'
import { IFormRef } from '../../../types'
import { getFormattedValidationErrors } from '../../../utils/mixins'
import Form from './Form'
import { fetchShowInvoice, resetShowInvoice } from '../../../store/features/invoices/showInvoiceSlice'
import { fetchUpdateInvoice, resetUpdateInvoice } from '../../../store/features/invoices/updateInvoiceSlice'

const Edit = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const formRef = useRef<IFormRef>(null)
  const dispatch = useAppDispatch()
  const [data, setData] = useState<IInvoiceParams | null>(null)

  const {
    isLoading: invoiceIsLoading,
    response: invoiceResponse,
    error: invoiceError
  } = useAppSelector(state => state.showInvoice)
  const { isLoading, response, error } = useAppSelector(state => state.updateInvoice)

  useEffect(() => {
    if (invoiceResponse === null && data === null) {
      ;(async () => {
        await dispatch(fetchShowInvoice(id as string))
      })()
    }
  }, [invoiceResponse, data, id, dispatch])

  const getSerializedData = (data: InvoiceType) => {
    console.log({
      company: data.company.id,
      amount: data.amount,
      months: data.months,
      continually: !!data.next_date,
      description: data.description ? data.description : '',
    })
    return {
      company: data.company.id,
      amount: data.amount,
      months: data.months,
      continually: !!data.next_date,
      description: data.description ? data.description : '',
    }
  }

  useEffect(() => {
    if (invoiceResponse !== null) {
      setData(getSerializedData(invoiceResponse.data))

      if (data !== null) {
        dispatch(resetShowInvoice())
      }
    }
  }, [invoiceResponse, data, dispatch])

  useEffect(() => {
    if (invoiceError !== null) {
      if (invoiceError.response.status === 401 && id) {
        ;(async () => {
          dispatch(resetShowInvoice())
          await dispatch(fetchShowInvoice(id))
        })()
      } else if (invoiceError.response.status === 404) {
        dispatch(resetShowInvoice())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t(invoiceError.data.message)
        })

        navigate('/invoices')
      } else {
        dispatch(resetShowInvoice())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t('errors.unknown')
        })

        navigate('/invoices')
      }
    }
  }, [invoiceError, id, dispatch, t, navigate])

  useEffect(() => {
    if (response !== null) {
      setData(getSerializedData(response.data))

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('updated')
      })

      dispatch(resetUpdateInvoice())
    }
  }, [response, t, dispatch])

  useEffect(() => {
    if (error !== null) {
      if (error.response.status === 422) {
        formRef.current!.setErrors(getFormattedValidationErrors(error))
      } else {
        showNotification({
          color: 'red',
          title: t('unsuccessful'),
          message: error.response.data ? error.response.data.message : t('errors.unknown')
        })
      }
    }
  }, [error, t])

  useEffect(() => {
    if (response !== null) {
      dispatch(resetUpdateInvoice())
      formRef.current!.reset()
    }
  }, [response, dispatch])

  const handleSubmit = async (params: IInvoiceParams) => {
    await dispatch(fetchUpdateInvoice({ id: id as string, params }))
  }

  return (
    <MainLayout>
      <LoadingOverlay visible={invoiceIsLoading || isLoading || !data} />
      {data && (
        <ContentBox title={t('content_actions.create')}>
          <Form
            id="edit-invoice-form"
            type={FormTypes.EDIT}
            ref={formRef}
            initialValues={data}
            onSubmit={handleSubmit}
          />
        </ContentBox>
      )}
      <ContentBox>
        <Grid>
          <Grid.Col span={6}>
            <BackListButton to="/invoices" />
          </Grid.Col>
          <Grid.Col span={6}>
            <Group position="right">
              <SaveButton form="edit-invoice-form" />
              <ResetButton form="edit-invoice-form" />
            </Group>
          </Grid.Col>
        </Grid>
      </ContentBox>
    </MainLayout>
  )
}

export default Edit
